import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import { BookletLinks, linksBooklets } from "../../pages/QuestionPage/review/bookletLock";
import { UserContext } from "../../utils/contexts/UserContext";
import { passageLockMessage } from "./passageErrors";

// The active BTS booklets that print a passage's questions: its detail's `booklets` ([{uuid, name}], those the user
// can not see included); [] when none print it, or from an older server without the field.
export const passageBooklets = (detail) =>
  Array.isArray(detail?.booklets) ? detail.booklets.filter((booklet) => booklet && typeof booklet === "object") : [];

/**
 * The calm notice a printed passage opens with: an active BTS booklet prints its questions, so neither the text nor
 * its questions change (the server refuses with 409 question_in_booklet) until an admin returns the booklet to draft.
 * It looks like the question page's lock notice (bookletLock.js BookletLockNotice), whose wording is the question's.
 * `booklets` [{uuid, name}]; `lock`: what they stop, "question" (the passage and its questions) or "passage" (no new
 * question joins it); `extra`: a sentence after it; `compact`: the smaller look inside a passage card (PassagePanel).
 * Whoever returns booklets to draft (an admin: linksBooklets) gets them as links, after a text that does not name them
 * again; anyone else reads their names and that an admin does it. Nothing without booklets.
 */
export default function PassageLockNotice({ booklets, lock = "question", extra = "", compact = false, className = "" }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  if (!booklets?.length) return null;
  const linked = linksBooklets(user, booklets);
  return (
    <div
      role="note"
      data-testid="passage-lock"
      className={`flex items-start bg-blue-50 leading-relaxed text-blue-950 ring-1 ring-inset ring-blue-600/15 ${
        compact ? "gap-2 rounded-xl px-3 py-2 text-xs" : "gap-3 rounded-2xl px-4 py-3 text-sm"
      } ${className}`}
    >
      <LockClosedIcon className={`shrink-0 text-blue-600 ${compact ? "h-4 w-4" : "mt-0.5 h-5 w-5"}`} aria-hidden="true" />
      <p className="min-w-0">
        {passageLockMessage(t, lock, booklets, linked)}
        {linked && <BookletLinks booklets={booklets} />}
        {extra && <span className="mt-1 block">{extra}</span>}
      </p>
    </div>
  );
}
